import { Section } from "@/layouts/Section";

export default function Privacy() {
  return (
    <div>
      <Section variant="light">
        <div className="max-w-[720px] mx-auto">
          <div className="section-label">Legal</div>
          <h1 className="section-heading mb-3">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground m-0">Effective: May 2026 &mdash; Version 2026-05</p>
        </div>
      </Section>

      <Section variant="muted">
        <div className="max-w-[720px] mx-auto flex flex-col gap-10">

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">1. Who This Policy Covers</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              This Privacy Policy describes how EasyReports (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses,
              and discloses information from and about teachers, school administrators, and visitors who
              use EasyReports.ca (&ldquo;the Service&rdquo;). We do not collect data directly from students;
              student data is entered by teachers and remains under their control.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">2. What Data We Collect</h2>
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Account Information</p>
                <p className="text-base leading-[1.75] text-muted-foreground m-0">
                  When you create an account, we collect your name, email address, and a hashed
                  identifier from our authentication provider (Clerk). We do not store your password.
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Report Card Data</p>
                <p className="text-base leading-[1.75] text-muted-foreground m-0">
                  Student names, grades, attendance records, learning skill ratings, subject marks, and
                  teacher comments that you enter into the Service. This data is stored in our database
                  in Toronto, Canada and never leaves Canada.
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Usage and Technical Data</p>
                <p className="text-base leading-[1.75] text-muted-foreground m-0">
                  Log data including IP addresses, browser type, pages visited, and timestamps. Error
                  reports captured by our error-monitoring service (Sentry). Subscription and billing
                  events processed by Stripe.
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">AI Interaction Data</p>
                <p className="text-base leading-[1.75] text-muted-foreground m-0">
                  When you use the AI comment-generation feature, we send a structured prompt containing
                  student report data (which may include student names and assessment information) to our
                  AI provider (Google LLC via Google Cloud Vertex AI). This processing occurs in the
                  Toronto, Canada region. We store the generated comment in our database for 3 years to
                  support audit and regeneration requests. Google does not use your prompts or outputs
                  to train its models by default and does not log prompts for model improvement, as
                  governed by the{" "}
                  <a href="https://cloud.google.com/terms/data-processing-addendum" className="text-primary underline underline-offset-2" target="_blank" rel="noopener noreferrer">Google Cloud Data Processing Addendum</a>.
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Cookies and Local Storage</p>
                <p className="text-base leading-[1.75] text-muted-foreground m-0">
                  We use a session cookie set by our authentication provider (Clerk) to keep you signed
                  in. This cookie is strictly necessary for the Service to function and is deleted when
                  you sign out. We also use your browser&rsquo;s local storage to remember your theme
                  preference and to cache your consent version so you are not shown the consent prompt
                  on every page load. We do not use advertising cookies or third-party tracking cookies.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">3. Why We Collect It</h2>
            <ul className="list-disc list-outside pl-5 flex flex-col gap-2 text-base leading-[1.75] text-muted-foreground m-0">
              <li>To operate the Service and provide the features you requested.</li>
              <li>To authenticate you and maintain your session securely.</li>
              <li>To process subscription payments and enforce plan limits.</li>
              <li>To monitor for errors and maintain service reliability.</li>
              <li>To send transactional emails (share invitations, payment reminders). We do not send marketing email.</li>
              <li>We will never sell, rent, or trade your personal information or student data to any third party for any purpose.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">4. Subprocessors</h2>
            <p className="text-base leading-[1.75] text-muted-foreground mb-4">
              We share data with the following third-party service providers to operate the Service:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 font-semibold text-foreground">Provider</th>
                    <th className="text-left py-2 pr-4 font-semibold text-foreground">Purpose</th>
                    <th className="text-left py-2 font-semibold text-foreground">Data Shared</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 pr-4 font-medium text-foreground">MongoDB Atlas</td>
                    <td className="py-2.5 pr-4">Database hosting (Toronto, Canada)</td>
                    <td className="py-2.5">All stored data</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 pr-4 font-medium text-foreground">Clerk</td>
                    <td className="py-2.5 pr-4">Authentication</td>
                    <td className="py-2.5">Email, name</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 pr-4 font-medium text-foreground">Stripe</td>
                    <td className="py-2.5 pr-4">Billing & payments</td>
                    <td className="py-2.5">Email, payment details</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 pr-4 font-medium text-foreground">Google Cloud (Vertex AI)</td>
                    <td className="py-2.5 pr-4">AI comment generation (Toronto, Canada)</td>
                    <td className="py-2.5">Student report data and assessment prompts</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-4 font-medium text-foreground">Resend</td>
                    <td className="py-2.5 pr-4">Transactional email</td>
                    <td className="py-2.5">Email address, message content</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">5. Data Residency</h2>
            <p className="text-base leading-[1.75] text-muted-foreground mb-3">
              All student data — including student names, grades, marks, comments, and AI-generated
              content — is stored and processed exclusively in Canada (Toronto). Our database
              (MongoDB Atlas) and AI processing (Google Cloud Vertex AI) are both hosted in the
              Toronto region. Student data never leaves Canada.
            </p>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              Some subprocessors (Clerk, Stripe, Resend) operate servers in the United States, but
              these services only process teacher account information, payment data, and email
              addresses — never student data. By using the Service you consent to the transfer of
              your teacher account information to these jurisdictions. We comply with{" "}
              <em>PIPEDA</em> accountability principles for cross-border data transfers.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">6. Data Retention</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 font-semibold text-foreground">Data Type</th>
                    <th className="text-left py-2 font-semibold text-foreground">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 pr-4">Account and report data</td>
                    <td className="py-2.5">Duration of account + 30 days after deletion</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 pr-4">AI-generated comments</td>
                    <td className="py-2.5">3 years from generation</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 pr-4">Activity logs</td>
                    <td className="py-2.5">2 years</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-4">Billing records</td>
                    <td className="py-2.5">7 years (legal requirement)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">7. Your Rights Under PIPEDA</h2>
            <p className="text-base leading-[1.75] text-muted-foreground mb-3">
              Under Canada&rsquo;s <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA),
              you have the right to:
            </p>
            <ul className="list-disc list-outside pl-5 flex flex-col gap-2 text-base leading-[1.75] text-muted-foreground m-0">
              <li><strong>Access</strong> the personal information we hold about you by contacting us.</li>
              <li><strong>Correct</strong> inaccurate personal information in your account via the Settings page.</li>
              <li><strong>Delete</strong> your account and associated data (subject to retention periods above).</li>
              <li><strong>Withdraw consent</strong> to our collection by deleting your account. Note that withdrawal of consent may mean you can no longer use the Service.</li>
              <li><strong>File a complaint</strong> with the Office of the Privacy Commissioner of Canada if you believe we have violated your rights.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">8. Security</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              We use industry-standard measures to protect your data, including TLS encryption in
              transit, encrypted storage at rest, and access controls that limit who can access
              production systems. No system is 100% secure. If you discover a security vulnerability,
              please disclose it responsibly to{" "}
              <a href="mailto:privacy@easyreports.ca" className="text-primary underline underline-offset-2">
                privacy@easyreports.ca
              </a>.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">9. Breach Notification</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              In the event of a security breach that poses a real risk of significant harm, we will
              notify affected users and report to the Office of the Privacy Commissioner of Canada
              within the timeframes required by PIPEDA and its Breach of Security Safeguards Regulations.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">10. Changes to This Policy</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              We may update this Privacy Policy from time to time. Material changes will be communicated
              via a consent prompt on your next login. The version and effective date at the top of this
              page indicate the current revision.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">11. Contact</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              Privacy questions and data access requests:{" "}
              <a href="mailto:privacy@easyreports.ca" className="text-primary underline underline-offset-2">
                privacy@easyreports.ca
              </a>
            </p>
          </div>

        </div>
      </Section>
    </div>
  );
}
